import * as React from "react";
import clsx from "clsx";
import { CheckOutlined } from "@ant-design/icons";
import { theme } from "antd";

export type FeatureImageButtonProps = {
  title: string;
  subtitle: string;
  image: string;
  selected: boolean;
  onClick: () => void;
};

export const FeatureImageButton: React.FC<FeatureImageButtonProps> = ({
  title,
  subtitle,
  image,
  selected,
  onClick,
}) => {
  const { token } = theme.useToken();
  const buttonToken = (token as { Button?: { colorPrimary?: string; colorPrimaryHover?: string } }).Button;
  const primaryColor = buttonToken?.colorPrimary ?? token.colorPrimary;
  const primaryHover = buttonToken?.colorPrimaryHover ?? token.colorPrimaryHover ?? primaryColor;

  const selectedAccentStyle = React.useMemo(
    () =>
      ({
        ["--precedent-primary"]: primaryColor,
        ["--precedent-primary-hover"]: primaryHover,
      }) as React.CSSProperties,
    [primaryColor, primaryHover]
  );

  return (
    <button
      type="button"
      onClick={onClick}
      style={selected ? selectedAccentStyle : undefined}
      aria-pressed={selected}
      className={clsx(
        "group relative w-[300px] overflow-hidden rounded-md border-2 bg-gray-50 text-left transition-[box-shadow,border-color] duration-[160ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
        selected
          ? "border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-[color:var(--precedent-primary)]"
          : "border-gray-200 shadow-sm hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-gray-500"
      )}
    >
      {selected ? (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-[1] rounded-md"
            style={{
              backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute right-3 top-3 z-[3] flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md"
            style={{ backgroundColor: "var(--precedent-primary)" }}
            aria-hidden
          >
            <CheckOutlined className="!text-[13px] leading-none" />
          </div>
        </>
      ) : (
        <div
          className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
          aria-hidden
        />
      )}

      <div className="relative z-0">
        <div className="box-border px-1.5 pt-1.5">
          <div className="flex aspect-video w-full max-h-[220px] items-center justify-center overflow-hidden rounded-sm bg-gray-50">
            <img
              alt={title}
              src={image}
              loading="lazy"
              decoding="async"
              className="max-h-full max-w-full object-contain"
              style={{ imageRendering: "auto" }}
            />
          </div>
        </div>
        <div className="px-3 py-2">
          <div className="mb-1 font-semibold text-gray-800">{title}</div>
          <div className="h-[5em] overflow-hidden text-ellipsis text-gray-600 [word-break:break-word]">{subtitle}</div>
        </div>
      </div>
    </button>
  );
};
